/*
 * Industry content — `ro` (Romanian).
 *
 * Romanian translation of the international industry set. Generic
 * time-registration framing, no belgium-specific pains/features, and
 * `compliance.slug` / `relatedArticles` pointing only at articles that exist
 * under `/en/` (the 4 market-agnostic posts). Pure data (NFR10).
 */
import type { Industry } from "./types";

export const INDUSTRIES_RO: Industry[] = [
  {
    slug: "construction",
    name: "Construcții",
    headline: "Pontaj pentru echipele de construcții — pontaj verificat prin locație, ore pe proiect și control al bugetului, într-o singură aplicație",
    intro: "Echipe răspândite pe șantiere, ore mâzgălite pe hârtie, ore ale subcontractanților imposibil de reconciliat. Done-it oferă fiecărui șantier o fișă de pontaj digitală etanșă care alimentează salarizarea cu un singur clic.",
    pains: [
      {
        title: "Nicio evidență fiabilă a cine a lucrat și unde",
        body: "Cu mai multe șantiere active și echipe care se rotesc, fișele de pontaj manuale sunt greșite din construcție. Lucrătorii rotunjesc în plus, uită înregistrări sau le completează zile mai târziu — lăsându-te cu cifre de salarizare pe care nu le poți susține.",
      },
      {
        title: "Depășirile de buget apar prea târziu",
        body: "Până observi că un șantier a consumat orele planificate, marja deja a dispărut. Nu există o numărătoare inversă în timp real — doar o surpriză neplăcută la momentul facturării.",
      },
      {
        title: "Pregătirea salarizării înghite ore în fiecare lună",
        body: "Colectarea fișelor de pontaj, reconcilierea lor cu proiectele și pregătirea exportului pentru salarizare durează zile. Fiecare înregistrare este o potențială eroare.",
      },
    ],
    features: [
      {
        title: "Pontaj verificat prin locație pe fiecare șantier",
        body: "O singură atingere pontează lucrătorul, verificat prin locație, și leagă orele de proiectul potrivit. Fără cititoare de carduri, fără terminale — doar smartphone-ul pe care echipa ta îl are deja.",
      },
      {
        title: "Ore rămase în timp real pe proiect",
        body: "Stabilește orele planificate ca buget când deschizi proiectul. Lucrătorii văd o numărătoare inversă a ceea ce a mai rămas; tu o vezi în timp real în panoul de control. Depășirile de buget sunt vizibile înainte să se întâmple.",
      },
      {
        title: "Gestionarea și prezența subcontractanților",
        body: "Adaugă subcontractanți la un proiect, atribuie-le pontajul și urmărește-le orele alături de propria echipă — în aceeași privire de ansamblu.",
      },
      {
        title: "Export pentru salarizare cu un singur clic",
        body: "Toate orele înregistrate — pe lucrător, pe proiect — se exportă într-o foaie de calcul curată pentru furnizorul tău de salarizare. Fără pregătire manuală, fără erori.",
      },
    ],
    compliance: {
      label: "Înregistrare digitală a timpului",
      title: "Pontaj verificat prin locație creat pentru șantierele de construcții",
      body: "Done-it oferă pontaj verificat prin locație, urmărirea timpului pe proiect și un export curat pentru salarizare destinate echipelor de construcții. Pentru echipele care operează pe șantiere belgiene, alimentează automat și pontajul la locul de muncă.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Echipa ajunge pe șantier", body: "Fiecare lucrător atinge o dată pentru a se ponta. GPS-ul confirmă locația, marchează ora înregistrării și leagă orele de proiect. Fără hârtie, fără terminal." },
      { step: "Vezi totul în timp real", body: "Ore, buget rămas, prezența subcontractanților și orice lucrare suplimentară raportată de pe șantier — toate în panoul tău de control, în timp real." },
      { step: "Sfârșitul lunii în câteva minute", body: "Exportă toate orele pe lucrător și pe proiect. Trimite-le furnizorului tău de salarizare. Done-it generează automat rapoarte curate, pregătite pentru audit." },
    ],
    stats: [
      { value: "1 atingere", label: "pentru a te ponta pe orice șantier — fără terminal, fără hârtie" },
      { value: "1 clic", label: "export pentru salarizare gata pentru furnizorul tău" },
      { value: "În timp real", label: "vizualizarea orelor rămase pe proiect și șantier" },
    ],
    quote: {
      text: "Echipa noastră se pontează singură pe șantier. La sfârșitul lunii, salarizarea pur și simplu se adună — am încetat complet să mai alergăm după fișe de pontaj.",
      who: "Patron, firmă de construcții · 14 angajați",
      result: "5 ore pe săptămână economisite la administrație",
    },
    relatedSlugs: ["installation", "maintenance", "field-service"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },

  {
    slug: "cleaning",
    name: "Curățenie",
    headline: "Pontaj pentru firmele de curățenie — pontaj verificat prin locație, dovadă a prezenței și salarizare într-o singură aplicație",
    intro: "Personalul de curățenie se deplasează între locații toată ziua. Done-it urmărește cine se află unde în timp real — pontaj verificat prin locație la fiecare punct de lucru, deducerea automată a pauzelor și un export curat pentru salarizare la sfârșitul lunii.",
    pains: [
      {
        title: "Dovedirea prezenței la fiecare locație",
        body: "Clienții și inspectorii se așteaptă la evidențe de prezență etanșe. Fișele manuale pot fi contestate. Fiecare înregistrare este verificată prin locație.",
      },
      {
        title: "Echipe răspândite în multe locații",
        body: "Cu personalul de curățenie care se deplasează între puncte de lucru pe parcursul zilei, nu ai nicio imagine în timp real a cine lucrează și unde — până când sună cineva.",
      },
      {
        title: "Erori de salarizare din orele rotunjite",
        body: "Fără ore exacte de intrare/ieșire, salarizarea este reconstruită din memorie. Orele sunt rotunjite în plus, costurile cresc și urmează dispute.",
      },
    ],
    features: [
      {
        title: "Pontaj la intrare și ieșire în timp real",
        body: "Lucrătorii se pontează la intrare și ieșire de pe telefon la fiecare locație, verificat prin locație; timpul este exact, cu înregistrarea pauzelor.",
        plan: "Time Basic",
      },
      {
        title: "Deducerea automată a pauzelor",
        body: "Pauzele obligatorii sunt scăzute automat din ziua de lucru — fără calcule manuale, fără dispute.",
        plan: "Time Basic",
      },
      {
        title: "Privire de ansamblu în timp real pe mai multe locații",
        body: "Vezi care persoane de la curățenie sunt la care locații chiar acum. Identifică lipsurile înainte ca clienții să o facă.",
      },
      {
        title: "Înregistrarea absențelor cu certificate medicale digitale",
        body: "Lucrătorii încarcă certificatele medicale și documentele de absență direct din aplicație. Creează tipurile de absență pe care echipa ta chiar le folosește.",
      },
      {
        title: "Export pentru salarizare cu un singur clic",
        body: "Marcajele de timp exacte ajung direct într-un export pregătit pentru salarizare pentru furnizorul tău. Fără transcriere, fără erori.",
      },
    ],
    compliance: {
      label: "Prezență verificată prin locație",
      title: "Prezență în timp real la fiecare locație de curățenie",
      body: "Done-it oferă echipelor de curățenie pontaj verificat prin locație la fiecare punct de lucru, deducerea automată a pauzelor și o imagine în timp real a cine se află pe ce locație chiar acum.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Persoana de la curățenie ajunge la locație", body: "O singură atingere o pontează. GPS-ul înregistrează punctul de lucru și marchează ora înregistrării. Înregistrarea pauzelor este integrată în aplicație." },
      { step: "Vezi fiecare locație în timp real", body: "Cine se află la care locație chiar acum — fără telefoane, fără presupuneri. Identifică lipsurile de acoperire înainte ca clienții să o facă." },
      { step: "Salarizarea înseamnă un singur clic", body: "Toate datele de prezență — ore exacte, locații și pauze — se exportă curat pentru furnizorul tău de salarizare." },
    ],
    stats: [
      { value: "1 atingere", label: "pentru a te ponta la orice locație — fără terminal" },
      { value: "În timp real", label: "vizualizarea cine se află pe ce locație chiar acum" },
      { value: "0", label: "înregistrări duble — orele ajung direct la salarizare" },
    ],
    quote: {
      text: "În sfârșit putem dovedi cine a făcut curățenie unde și când. Gata cu fișele de prezență, gata cu disputele cu clienții — GPS-ul se ocupă de asta.",
      who: "Manager, firmă de curățenie · 9 angajați",
      result: "Zero rotunjire în plus a orelor",
    },
    relatedSlugs: ["maintenance", "field-service", "construction"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },

  {
    slug: "installation",
    name: "Instalații",
    headline: "Pontaj pentru echipele de instalații — ore pe proiect, navigație și control al bugetului într-o singură aplicație",
    intro: "Trimite-ți montatorii la adresa potrivită cu tot ce le trebuie, leagă automat fiecare oră lucrată de lucrarea potrivită și vezi bugetul rămas înainte ca șantierul să se închidă. Done-it este cea mai simplă cale de a menține proiectele de instalații la timp și în limita costurilor.",
    pains: [
      {
        title: "Informațiile despre lucrare nu ajung la timp pe teren",
        body: "Echipele ajung pe un șantier fără adresă, persoană de contact sau detalii despre scop. Primul telefon înseamnă zece minute de timp facturabil pierdute înainte ca o singură oră să fie lucrată.",
      },
      {
        title: "Orele nu sunt legate de proiectul potrivit",
        body: "Când lucrătorii își completează orele manual la sfârșitul zilei — dacă le completează — orele ajung pe lucrarea greșită sau dispar complet. Facturile nu corespund realității.",
      },
      {
        title: "Depășirile de buget observate prea târziu",
        body: "Fără o numărătoare inversă în timp real a orelor rămase pe proiect, afli că o lucrare a depășit bugetul când închizi factura — nu cât încă poți reacționa.",
      },
      {
        title: "Orele subcontractanților greu de urmărit",
        body: "Subcontractanții care lucrează pe același șantier folosesc sisteme diferite, completează orele separat sau deloc. Reconcilierea la sfârșitul lunii este un proces lent și predispus la erori.",
      },
    ],
    features: [
      {
        title: "Informațiile despre proiect partajate direct în aplicație",
        body: "Lucrătorii deschid proiectul pe telefon: adresă, ore planificate, persoană de contact și fișiere încărcate (planuri, instrucțiuni, PDF-uri). Tot ce le trebuie este acolo înainte să ajungă.",
        plan: "Project Basic",
      },
      {
        title: "Navigație către șantier cu un singur buton",
        body: "Atinge o dată și telefonul navighează către adresa proiectului — integrat în aplicație, fără o aplicație separată de hartă.",
      },
      {
        title: "Numărătoare inversă a orelor rămase pe proiect",
        body: "Stabilește orele planificate când deschizi proiectul. Lucrătorii văd câte ore au rămas; tu vezi starea în timp real în panoul de control. Depășirile sunt vizibile înainte să se întâmple.",
        plan: "Project Basic",
      },
      {
        title: "Pontajul lucrătorului pe proiect",
        body: "Fiecare lucrător se pontează pe proiectul potrivit. Știi mereu cine se află pe ce șantier chiar acum.",
        plan: "Project Basic",
      },
      {
        title: "Înregistrarea lucrărilor și materialelor suplimentare",
        body: "Când scopul crește pe șantier, lucrătorii înregistrează lucrările și materialele suplimentare în timp real — cu text și fotografii — astfel încât nimic nu se facturează din memorie.",
        plan: "Project Basic",
      },
    ],
    compliance: {
      label: "Înregistrare digitală a timpului",
      title: "Pontaj verificat prin locație pe proiect pentru echipele de instalații",
      body: "Done-it oferă echipelor de instalații pontaj verificat prin locație pe proiect, o numărătoare inversă în timp real a orelor rămase și export automat pentru salarizare — acoperind orice cerință locală de înregistrare a timpului. Pentru echipele care lucrează pe șantiere de construcții belgiene, se integrează și cu CheckIn@Work.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Lucrătorul deschide proiectul pe telefon", body: "Adresa, contactul, scopul și fișierele încărcate sunt chiar acolo. Navighează cu o singură atingere — fără telefoane la birou pentru indicații." },
      { step: "Orele se înregistrează automat pe lucrarea potrivită", body: "Pontajul leagă fiecare minut lucrat de proiectul corect. Bugetul rămas se actualizează în timp real." },
      { step: "Lucrările suplimentare sunt înregistrate pe șantier", body: "Modificările de scop, materialele suplimentare și întârzierile sunt înregistrate cu text și fotografii în momentul în care se întâmplă — gata pentru factură." },
    ],
    stats: [
      { value: "În timp real", label: "vizualizarea orelor rămase pe proiect" },
      { value: "0", label: "telefoane la birou pentru adresele șantierelor" },
      { value: "1 clic", label: "navighează către orice adresă de proiect din aplicație" },
    ],
    quote: {
      text: "Orele ajung automat pe proiectul potrivit. Calculul costurilor pe lucrare a devenit dintr-odată ușor — putem vedea marja reală înainte să închidem o lucrare.",
      who: "Patron, firmă de instalații · 6 angajați",
      result: "Cost real pe lucrare, în timp real",
    },
    relatedSlugs: ["construction", "electricians", "hvac"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },

  {
    slug: "field-service",
    name: "Servicii pe teren",
    headline: "Pontaj pentru echipele de servicii pe teren — kilometraj, cheltuieli și ore din mers, toate într-o singură aplicație",
    intro: "Tehnicieni care vizitează mai mulți clienți pe zi, bonuri în torpedouri, kilometraj înregistrat din memorie. Done-it captează ore, kilometri și cheltuieli pe teren — atașate lucrării potrivite în momentul în care se întâmplă.",
    pains: [
      {
        title: "Kilometraj înregistrat zile mai târziu — dacă e înregistrat",
        body: "A le cere tehnicienilor să-și reconstituie rutele la sfârșitul săptămânii înseamnă rambursări inexacte, dispute cu clienții și un teanc tot mai mare de kilometri nedecontați.",
      },
      {
        title: "Cheltuieli pe bonuri de hârtie care dispar",
        body: "Un bon dintr-un torpedou este un cost care s-ar putea să nu ajungă niciodată într-o factură. Costul real pe lucrare este mereu subestimat.",
      },
      {
        title: "Nicio imagine în timp real a locului în care se află echipa",
        body: "Fără pontaje în timp real, afli unde se află un tehnician doar când ceva merge prost și sună cineva.",
      },
      {
        title: "Greu de văzut marja reală pe lucrare",
        body: "Când manopera, deplasarea și cheltuielile sunt urmărite în sisteme separate — sau deloc — nu există niciodată o singură cifră de cost pe lucrare.",
      },
    ],
    features: [
      {
        title: "Captarea automată a kilometrajului (Trax-it)",
        body: "GPS-ul înregistrează plecarea, sosirea și distanța exactă pentru fiecare deplasare. Configurează tipuri de transport (mașină proprie, vehicul de firmă, transport în comun) cu propriile tarife de rambursare. Fără jurnal de bord manual.",
        plan: "Add-on",
      },
      {
        title: "Gestionarea cheltuielilor pe teren",
        body: "Lucrătorii fotografiază un bon pe teren; este atașat lucrării și pregătit pentru rambursare — nu urmărit la sfârșitul lunii.",
        plan: "Time Basic",
      },
      {
        title: "Control al costurilor legat de proiect",
        body: "Orele, kilometrajul și cheltuielile se adună pe lucrare în timp real. Costul pe lucrare este vizibil cât lucrarea este încă deschisă.",
        plan: "Project Basic",
      },
      {
        title: "Pontaj verificat prin locație și privire de ansamblu a echipei în timp real",
        body: "Fiecare tehnician se pontează la sediul clientului. Vezi cine se află unde, chiar acum — fără telefoane, fără presupuneri.",
      },
      {
        title: "Export pentru salarizare cu un singur clic",
        body: "Orele și rambursările de kilometraj se exportă curat pentru furnizorul tău de salarizare — cu tarifele de kilometraj configurate aplicate automat.",
      },
    ],
    compliance: {
      label: "Urmărire digitală a timpului și kilometrajului",
      title: "Ore prin GPS și kilometraj automat pentru echipele de servicii pe teren",
      body: "Done-it captează automat kilometrajul pentru fiecare deplasare, aplică tarifele de rambursare configurate de tine și înregistrează fiecare kilometru pentru clientul sau proiectul potrivit. Hotărârea UE în cazul Tyco înseamnă că deplasarea de acasă la primul client contează ca timp de lucru — Done-it urmărește și asta.",
      slug: "how-to-track-mileage-for-field-teams",
    },
    workflow: [
      { step: "Tehnicianul pleacă spre primul apel", body: "Pontajul pornește jurnalul deplasării. Kilometrii sunt captați automat de la plecare la sosire — fără înregistrare manuală." },
      { step: "La șantier", body: "Orele se leagă de proiectul potrivit. Lucrările suplimentare, materialele și bonurile sunt înregistrate în aplicație în momentul în care se întâmplă." },
      { step: "Sfârșitul lunii fără haos", body: "Fiecare oră, kilometru și cheltuială este deja atașată lucrării corecte. Exportă pentru salarizare și facturează cu cifre de cost reale." },
    ],
    stats: [
      { value: "Automat", label: "captarea kilometrajului — fără jurnal de bord manual" },
      { value: "În timp real", label: "cost pe lucrare: ore + deplasare + cheltuieli" },
      { value: "0", label: "kilometri pierduți — fiecare deplasare captată automat" },
    ],
    quote: {
      text: "Kilometrajul era o bătaie de cap lunară. Acum se înregistrează singur — fiecare kilometru este atașat clientului potrivit înainte să ne întoarcem măcar la birou.",
      who: "Manager de operațiuni, firmă de servicii pe teren · 11 angajați",
      result: "Cost exact pe lucrare, de fiecare dată",
    },
    relatedSlugs: ["hvac", "maintenance", "plumbing"],
    relatedArticles: [
      "how-to-track-mileage-for-field-teams",
      "gps-tracking-field-workers-privacy-gdpr",
    ],
  },

  {
    slug: "hvac",
    name: "HVAC",
    headline: "Pontaj pentru tehnicienii HVAC — calculul costurilor pe lucrare, piese și deplasare într-o singură aplicație",
    intro: "Fiecare apel de service are trei costuri: manoperă, piese și deplasare. Done-it le captează pe toate trei pe teren și le adună pe lucrarea potrivită — așa că îți cunoști marja înainte să închizi factura.",
    pains: [
      {
        title: "Apelurile de service sunt greu de calculat cu exactitate ca și cost",
        body: "Manopera, piesele și deplasarea trăiesc în sisteme diferite — sau buzunare diferite. Costul real al unei intervenții devine clar abia mult după ce tehnicianul a plecat de pe șantier.",
      },
      {
        title: "Nicio dovadă a timpului de service",
        body: "Când un client contestă cât a durat o reparație, o fișă de pontaj pe hârtie este ușor de contestat. Marcajele de pontaj la intrare și ieșire confirmate prin GPS nu sunt.",
      },
      {
        title: "Contractele de mentenanță erodează marja în tăcere",
        body: "Pe contractele de mentenanță recurentă, micile depășiri se acumulează de-a lungul multor vizite. Fără un contor de ore în timp real, afli că contractul este în pierdere abia la reînnoire.",
      },
      {
        title: "Tehnicienii uită să înregistreze pauzele",
        body: "Înregistrările manuale de timp ratează pauzele, ducând la supraplată și o salarizare care nu corespunde realității.",
      },
    ],
    features: [
      {
        title: "Pontaj bazat pe proiect",
        body: "Fiecare apel de service sau contract de mentenanță este un proiect. Orele, deplasarea și materialele se adună în timp real — oferindu-ți vizibilitate a marjei în timp real.",
        plan: "Project Basic",
      },
      {
        title: "Lucrări și materiale suplimentare, înregistrate pe șantier",
        body: "Când o lucrare crește, tehnicienii înregistrează lucrările suplimentare și piesele cu text și fotografii înainte să plece. Nimic nu se facturează din memorie.",
        plan: "Project Basic",
      },
      {
        title: "Kilometraj și costuri de transport",
        body: "Kilometrii pe intervenție sunt înregistrați automat și alocați ca și cost lucrării. Configurează tarifele de rambursare pe tip de vehicul.",
        plan: "Add-on",
      },
      {
        title: "Deducerea automată a pauzelor",
        body: "Pauzele obligatorii sunt scăzute automat din timpul total — fără dispute, fără erori de salarizare.",
      },
      {
        title: "Pontaj verificat prin locație și dovadă a prezenței",
        body: "Pontaj la intrare confirmat prin GPS, cu marcaj de timp, la fiecare șantier. Fiecare înregistrare este verificată prin locație.",
      },
    ],
    compliance: {
      label: "Urmărire digitală a timpului și costurilor",
      title: "Calculul complet al costurilor pe lucrare pentru apelurile de service HVAC",
      body: "Done-it oferă echipelor HVAC pontaj verificat prin locație pe fiecare apel de service, vizibilitate a costurilor în timp real pe lucrare (manoperă + deplasare + piese) și un export curat pentru salarizare — îndeplinind orice cerință locală de înregistrare a timpului fără echipamente suplimentare.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Tehnicianul se pontează la șantier", body: "GPS-ul marchează ora sosirii și leagă orele de apelul de service sau de contractul de mentenanță. Fără hârtie, fără terminal." },
      { step: "Lucrările suplimentare sunt înregistrate pe loc", body: "Piesele folosite, orele suplimentare și modificările de scop sunt înregistrate în aplicație cu fotografii — înainte de a părăsi șantierul." },
      { step: "Costul lucrării este vizibil imediat", body: "Manopera, deplasarea și materialele se adună pe proiect în timp real. Vezi marja înainte să emiți factura." },
    ],
    stats: [
      { value: "3", label: "tipuri de cost captate pe apel: manoperă, deplasare, piese" },
      { value: "În timp real", label: "vizibilitate a marjei pe contract de service" },
      { value: "0", label: "surprize la momentul facturării — costuri vizibile în timp real" },
    ],
    quote: {
      text: "În sfârșit știm cât ne costă cu adevărat fiecare contract de service. Surprizele legate de marjă au încetat din ziua în care am început să folosim Done-it.",
      who: "Patron, firmă HVAC · 7 tehnicieni",
      result: "Cost real pe apel de service, în timp real",
    },
    relatedSlugs: ["installation", "electricians", "plumbing"],
    relatedArticles: [
      "how-to-track-mileage-for-field-teams",
      "time-tracking-for-field-teams-complete-guide",
    ],
  },

  {
    slug: "electricians",
    name: "Electricieni",
    headline: "Pontaj pentru electricieni — ore pe proiect, control al bugetului și navigație într-o singură aplicație",
    intro: "Ore pe proiect, o numărătoare inversă în timp real a bugetului rămas și navigație către șantier cu un singur buton — creat pentru echipele mici de electricieni care au nevoie de control al proiectului fără povara administrativă.",
    pains: [
      {
        title: "Orele nu sunt legate de proiectul potrivit",
        body: "Când electricienii lucrează la mai multe lucrări într-o săptămână, alocarea manuală a orelor la sfârșitul zilei duce la costuri atribuite greșit, dispute de facturare și o salarizare pe care nu o poți explica.",
      },
      {
        title: "Depășirile de buget observate prea târziu",
        body: "Fără o imagine în timp real a orelor consumate față de orele bugetate, afli că un proiect a depășit bugetul când emiți factura — nu când încă puteai reacționa.",
      },
      {
        title: "Timp pierdut găsind șantierul",
        body: "Adrese tastate într-o aplicație separată, telefoane la birou pentru indicații, sosiri târzii la sediile clienților. Fiecare minut pierdut este un minut facturabil pierdut.",
      },
      {
        title: "Orele subcontractanților greu de reconciliat",
        body: "La instalațiile mai mari, electricienii subcontractanți își urmăresc orele separat. Obținerea unei imagini combinate a costurilor de manoperă la sfârșitul unui proiect este o muncă manuală, predispusă la erori.",
      },
    ],
    features: [
      {
        title: "Ore pe proiect cu buget rămas în timp real",
        body: "Fiecare minut lucrat este legat automat de proiectul potrivit. O numărătoare inversă în timp real arată câte ore au rămas din buget — pentru tine și pentru lucrătorul de pe șantier.",
        plan: "Project Basic",
      },
      {
        title: "Navigație către șantier cu un singur buton",
        body: "Adresa proiectului este în aplicație. O singură atingere deschide navigația — fără căutări, fără o aplicație separată de hartă, fără telefoane la birou.",
      },
      {
        title: "Pontajul lucrătorului pe proiect",
        body: "Fiecare electrician se pontează pe lucrarea potrivită. Știi mereu cine se află pe ce șantier chiar acum.",
        plan: "Project Basic",
      },
      {
        title: "Lucrări suplimentare înregistrate pe șantier",
        body: "Când scopul se schimbă — un circuit neprevăzut, materiale suplimentare — este înregistrat în aplicație cu fotografii, înainte ca echipa să plece.",
        plan: "Project Basic",
      },
      {
        title: "Urmărirea orelor suplimentare cu compensare flexibilă",
        body: "Orele suplimentare sunt înregistrate în momentul în care se întâmplă. Lucrătorii aleg să le primească plătite sau convertite în timp liber.",
        plan: "Time Pro",
      },
    ],
    compliance: {
      label: "Pontaj verificat prin locație pe proiect",
      title: "Control al bugetului în timp real și pontaj verificat prin locație pentru echipele de electricieni",
      body: "Done-it oferă electricienilor pontaj verificat prin locație pe proiect, un buget de ore rămase în timp real și export pentru salarizare — satisfăcând orice cerință locală de înregistrare a timpului. Pentru echipele de pe șantierele de construcții belgiene, se integrează și cu CheckIn@Work.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Electricianul navighează către lucrare", body: "Adresa și scopul sunt deja în aplicație. O singură atingere deschide navigația — fără telefoane, fără confuzie." },
      { step: "Orele se leagă automat de proiectul potrivit", body: "Pontajul la șantier leagă fiecare minut lucrat de proiectul corect. Contorul de ore rămase se actualizează în timp real." },
      { step: "Modificările de scop rămân în evidență", body: "Lucrările și materialele suplimentare sunt înregistrate pe șantier, înainte de plecare. Factura reflectă ce s-a întâmplat cu adevărat." },
    ],
    stats: [
      { value: "În timp real", label: "vizualizarea orelor rămase pe proiect" },
      { value: "0", label: "telefoane la birou pentru adresele șantierelor" },
      { value: "1 atingere", label: "pentru a te ponta la orice șantier — fără terminal" },
    ],
    quote: {
      text: "Știm în timp real dacă un proiect depășește bugetul. Doar atât ne-a scutit de două surprize neplăcute anul acesta.",
      who: "Patron, firmă de electrice · 5 electricieni",
      result: "Depășiri de buget prinse înainte să se întâmple",
    },
    relatedSlugs: ["installation", "hvac", "plumbing"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "how-to-track-mileage-for-field-teams",
    ],
  },

  {
    slug: "plumbing",
    name: "Instalații sanitare",
    headline: "Pontaj pentru echipele de instalații sanitare — pontaj rapid, salarizare exactă și dovadă a prezenței pentru fiecare intervenție",
    intro: "Intervenții de urgență, mai multe lucrări pe zi, ore completate din memorie la sfârșitul săptămânii — sau deloc. Done-it transformă asta într-o operațiune de o singură atingere la fiecare șantier și o salarizare de un singur clic la sfârșitul lunii.",
    pains: [
      {
        title: "Orele de intervenție reconstruite din memorie",
        body: "Cu mai multe lucrări pe zi, instalatorii rareori au timp să completeze fișe de pontaj exacte. Orele sunt rotunjite sau uitate, iar salarizarea este în cel mai bun caz o estimare.",
      },
      {
        title: "Nicio dovadă a timpului pe șantier",
        body: "Când un client contestă cât a durat o reparație, nu există o evidență obiectivă. Un pontaj digital confirmat prin GPS este ușor de produs — o fișă de pontaj pe hârtie nu.",
      },
      {
        title: "Kilometrajul între lucrări neurmărit",
        body: "Deplasarea între intervenții se adună de-a lungul săptămânii și lunii, dar fără captare automată este rareori facturată sau rambursată corect.",
      },
      {
        title: "Pregătirea salarizării este lentă",
        body: "La sfârșitul lunii, alergatul după fișe de pontaj, corectarea erorilor și construirea exportului pentru salarizare consumă timp pe care echipa ta administrativă nu ar trebui să-l cheltuiască.",
      },
    ],
    features: [
      {
        title: "Pontaj verificat prin locație cu o singură atingere la fiecare intervenție",
        body: "Sosirea și plecarea înregistrate cu o singură atingere, cu verificarea locației. Suficient de rapid pentru apeluri de urgență; suficient de precis pentru dispute.",
        plan: "Time Basic",
      },
      {
        title: "Deducerea automată a pauzelor",
        body: "Pauzele obligatorii sunt scăzute automat din timpul total — nimic de completat, fără erori de salarizare.",
        plan: "Time Basic",
      },
      {
        title: "Kilometraj captat pe deplasare",
        body: "Configurează tipuri de transport și tarife de rambursare. Kilometrii se înregistrează automat și se leagă de clientul sau proiectul corect.",
        plan: "Add-on",
      },
      {
        title: "Gestionarea absențelor și a certificatelor medicale",
        body: "Lucrătorii trimit certificate medicale digital din aplicație. Creează tipurile de absență pe care echipa ta chiar le folosește, inclusiv întârzieri cauzate de vreme.",
      },
      {
        title: "Export pentru salarizare cu un singur clic",
        body: "Toate orele și datele de kilometraj se exportă curat pentru furnizorul tău de salarizare — fără pregătire manuală, fără Excel.",
        plan: "Time Basic",
      },
    ],
    compliance: {
      label: "Înregistrare digitală a timpului",
      title: "Pontaj rapid verificat prin locație pentru fiecare intervenție",
      body: "Done-it acoperă orice cerință locală de înregistrare a timpului pentru echipele de instalații sanitare — pontaj verificat prin locație cu o singură atingere pe intervenție, deducerea automată a pauzelor și un export curat pentru salarizare. Fără fișe de pontaj pe hârtie, fără reconstrucție manuală.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Instalatorul ajunge la intervenție", body: "O singură atingere îl pontează. GPS-ul înregistrează locația și ora. Suficient de rapid pentru o lucrare de urgență." },
      { step: "Plecarea este atinsă la ieșire", body: "Pontajul de ieșire închide înregistrarea de timp. Kilometrajul către următoarea lucrare începe să se înregistreze automat." },
      { step: "Sfârșitul lunii înseamnă un singur clic", body: "Toate intervențiile, orele și kilometrii se exportă curat. Pregătirea salarizării scade de la o zi la câteva minute." },
    ],
    stats: [
      { value: "1 atingere", label: "pentru a te ponta — suficient de rapid pentru apeluri de urgență" },
      { value: "1 clic", label: "export pentru salarizare către furnizorul tău" },
      { value: "0", label: "ore reconstruite din memorie la sfârșitul lunii" },
    ],
    quote: {
      text: "Obișnuiam să petrecem o dimineață la sfârșitul lunii reconstruind orele. Acum doar exportăm și trimitem. Furnizorul de salarizare le primește curate.",
      who: "Patron, firmă de instalații sanitare · 4 instalatori",
      result: "Pregătirea salarizării redusă de la ore la minute",
    },
    relatedSlugs: ["hvac", "electricians", "field-service"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "how-to-track-mileage-for-field-teams",
    ],
  },

  {
    slug: "landscaping",
    name: "Amenajări peisagistice",
    headline: "Pontaj pentru firmele de amenajări peisagistice — echipe sezoniere, mai multe locații și kilometraj într-o singură privire de ansamblu",
    intro: "Sezonul de vârf înseamnă multe locații, echipe care se schimbă și mulți kilometri. Done-it ține fiecare oră, fiecare absență și fiecare deplasare organizate — ca să te poți concentra pe muncă, nu pe hârtii.",
    pains: [
      {
        title: "Echipele sezoniere sunt complexe de gestionat",
        body: "Lucrătorii temporari, programele schimbătoare și dimensiunile variabile ale echipelor fac aproape imposibilă urmărirea exactă a orelor cu hârtia. Până reconciliezi la sfârșitul lunii, mereu lipsește ceva.",
      },
      {
        title: "Ore răspândite în multe locații",
        body: "O echipă care vizitează trei sau patru locații pe zi generează ore care trebuie împărțite corect. Înregistrarea manuală duce la atribuiri greșite și la o salarizare contestată.",
      },
      {
        title: "Opririle cauzate de vreme sunt greu de documentat",
        body: "Întreruperea din cauza vremii nefavorabile este un eveniment real în amenajările peisagistice. Fără o evidență digitală, documentarea opririlor și ajustarea plății este lentă și predispusă la erori.",
      },
      {
        title: "Kilometrajul între locații rămâne neurmărit",
        body: "Dube de firmă, vehicule personale și transport în comun circulă între locații toată ziua. Fără captare automată, acei kilometri nu ajung niciodată în imaginea costurilor.",
      },
    ],
    features: [
      {
        title: "Privire de ansamblu a echipei pe mai multe locații",
        body: "Vezi întreaga echipă, toate locațiile în timp real. Află cine lucrează și unde, chiar acum — nu la sfârșitul lunii.",
      },
      {
        title: "Programarea turelor pentru echipe sezoniere",
        body: "Planifică cine lucrează și unde, gestionează modificările de ultim moment și trimite programul pe telefoanele echipei — fără un telefon către fiecare persoană.",
        plan: "Time Pro",
      },
      {
        title: "Tipuri de absență personalizate",
        body: "Creează tipuri de absență personalizate pentru sectorul tău: opriri sezoniere cauzate de vreme, concediu parental, zile de concediu medical. Lucrătorii încarcă documentația digital din aplicație.",
      },
      {
        title: "Kilometraj cu urmărirea transportului în comun",
        body: "Configurează tipuri de transport (dubă de firmă, mașină personală, șofer vs. pasager în transportul în comun) cu propriile tarife. Kilometrii se înregistrează automat pe deplasare.",
        plan: "Add-on",
      },
      {
        title: "Urmărirea orelor suplimentare cu conversie în concediu",
        body: "Orele suplimentare din sezonul de vârf se acumulează rapid. Fiecare lucrător poate alege să fie plătit sau să strângă orele ca timp liber — urmărite transparent.",
        plan: "Time Pro",
      },
    ],
    compliance: {
      label: "Înregistrare digitală a timpului",
      title: "Pontaj verificat prin locație și urmărirea absențelor pentru echipele sezoniere de amenajări peisagistice",
      body: "Done-it oferă echipelor de amenajări peisagistice înregistrarea digitală a timpului pentru toate locațiile și personalul sezonier — pontaj verificat prin locație, tipuri de absență personalizate pentru opririle cauzate de vreme și un export curat pentru salarizare care satisface orice cerință locală.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Echipa se pontează la prima locație", body: "GPS-ul înregistrează sosirea. Orele se împart automat pe locația și proiectul potrivit — fără alocare manuală la sfârșitul zilei." },
      { step: "Vremea oprește lucrul", body: "Șeful de echipă înregistrează oprirea din aplicație — o evidență digitală curată, fără hârtii suplimentare." },
      { step: "Sfârșitul lunii: toate locațiile, toată echipa, un singur export", body: "Orele, absențele și kilometrajul de la fiecare locație și fiecare membru al echipei se exportă curat pentru salarizare." },
    ],
    stats: [
      { value: "Automat", label: "captarea kilometrajului — gata cu jurnalul de bord" },
      { value: "În timp real", label: "privire de ansamblu a echipei pe toate locațiile" },
      { value: "1 clic", label: "export pentru salarizare pentru toate locațiile și toată echipa" },
    ],
    quote: {
      text: "Sezonul de vârf însemna haos la salarizare. Acum șeful nostru de echipă verifică aplicația, toată lumea este contabilizată, iar eu export orele în ultima zi a lunii.",
      who: "Patron, firmă de amenajări peisagistice · 12 lucrători sezonieri",
      result: "Gata cu reconstrucția salarizării de la sfârșitul lunii",
    },
    relatedSlugs: ["maintenance", "construction", "field-service"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "gps-tracking-field-workers-privacy-gdpr",
    ],
  },

  {
    slug: "maintenance",
    name: "Mentenanță",
    headline: "Pontaj pentru echipele de mentenanță — vizite recurente la șantiere, cost pe contract și dovadă a prezenței într-o singură aplicație",
    intro: "Contractele de mentenanță recurentă trăiesc sau mor în funcție de vizibilitatea costurilor. Done-it înregistrează fiecare vizită, urmărește orele față de bugetul contractului și îți oferă ție — și clienților tăi — dovada prezenței pentru fiecare apel.",
    pains: [
      {
        title: "Nicio vizibilitate a costurilor pe contract de mentenanță",
        body: "Când orele pentru vizitele recurente nu sunt urmărite pe contractul specific, nu știi dacă un client este profitabil până la analiza de sfârșit de an — moment în care este prea târziu să mai repreciezi.",
      },
      {
        title: "Clienții se așteaptă la dovada a ceea ce s-a făcut",
        body: "Administratorii de clădiri și proprietarii doresc tot mai mult evidențe de prezență: cine a venit, când a sosit, ce a făcut. Un jurnal pe hârtie prezentat săptămâni mai târziu nu satisface cerințele SLA.",
      },
      {
        title: "Vizitele recurente greu de programat și înregistrat",
        body: "Turele rotative la multe locații recurente — cu personal care se acoperă reciproc — creează un puzzle de programare și urmărire a orelor pe care procesele manuale nu îl pot rezolva fiabil.",
      },
      {
        title: "Opririle cauzate de vreme afectează și mentenanța în exterior",
        body: "Întreținerea spațiilor verzi, mentenanța fațadelor și lucrările la facilitățile exterioare se confruntă toate cu opriri legate de vreme. Documentarea lor corectă necesită o urmă digitală.",
      },
    ],
    features: [
      {
        title: "Urmărirea costurilor bazată pe contract",
        body: "Fiecare contract de mentenanță este un proiect. Orele se înregistrează automat pe el, oferindu-ți o cifră de cost în timp real și o numărătoare inversă a orelor rămase pentru fiecare client.",
        plan: "Project Basic",
      },
      {
        title: "Dovadă a prezenței confirmată prin GPS",
        body: "Pontajele verificate prin locație, cu marcaj de timp, sunt o evidență a cine s-a aflat pe șantier, când și pentru cât timp — gata de partajat cu clienții la cerere.",
      },
      {
        title: "Actualizări cu fotografii de pe șantier",
        body: "Lucrătorii raportează probleme, sarcini finalizate sau lucrări suplimentare direct din aplicație — cu text și fotografii. Clienții primesc o evidență în timp real; tu ai documentația pentru facturare.",
        plan: "Project Basic",
      },
      {
        title: "Programarea turelor recurente",
        body: "Planifică vizitele recurente pe toate contractele, atribuie lucrători și gestionează înlocuirile — fără un telefon către fiecare persoană.",
        plan: "Time Pro",
      },
      {
        title: "Tipuri de absență pentru mentenanța în exterior",
        body: "Configurează opririle cauzate de vreme și alte tipuri de absență. Lucrătorii le înregistrează din aplicație; documentația se încarcă digital.",
      },
    ],
    compliance: {
      label: "Pontaj bazat pe contract",
      title: "Vizibilitate a costurilor în timp real pe contract de mentenanță",
      body: "Done-it oferă echipelor de mentenanță dovadă a prezenței confirmată prin GPS pe fiecare contract, tipuri de absență personalizate și o imagine în timp real a costurilor pe client recurent — satisfăcând orice cerință locală de înregistrare a timpului fără echipamente suplimentare.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Lucrătorul ajunge la locația de mentenanță", body: "Pontajul verificat prin locație înregistrează ora sosirii și leagă orele de contractul corect. Dovada prezenței este creată automat." },
      { step: "Sarcini finalizate, probleme înregistrate", body: "Lucrările finalizate și orice probleme sunt raportate din aplicație cu fotografii. Clienții pot vedea exact ce s-a făcut." },
      { step: "Costul pe contract vizibil imediat", body: "Orele se acumulează față de bugetul contractului în timp real. Cunoști marja înainte ca contractul să se reînnoiască." },
    ],
    stats: [
      { value: "În timp real", label: "cost pe contract de mentenanță" },
      { value: "GPS", label: "dovadă a prezenței gata pentru clienți la cerere" },
      { value: "0", label: "reînnoiri de contract fără a-ți cunoaște marja" },
    ],
    quote: {
      text: "Putem spune unui administrator de clădire exact cine s-a aflat pe șantier și când, în două minute. Acel nivel de dovadă a schimbat modul în care ne văd clienții.",
      who: "Manager de operațiuni, firmă de mentenanță · 8 lucrători",
      result: "Disputele cu clienții reduse la zero",
    },
    relatedSlugs: ["cleaning", "landscaping", "field-service"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "gps-tracking-field-workers-privacy-gdpr",
    ],
  },
  {
    slug: "retail",
    name: "Magazine și retail",
    headline: "Pontaj pentru magazine — pontare pe tură, înlocuiri și un export gata de salarizare, într-o singură aplicație",
    intro: "Ture de dimineață, ture de seară și mereu cineva care ține locul altcuiva. Done-it oferă fiecărui magazin o evidență digitală clară a cui a lucrat în ce tură — pontat de pe propriul telefon, gata pentru salarizare la final de lună.",
    pains: [
      {
        title: "Schimburile de tură nu ajung niciodată la salarizare",
        body: "Cineva ține locul unui coleg bolnav, altcineva rămâne o oră în plus la închidere. Se reține pe un bilețel, sau deloc — iar salarizarea e greșită înainte să începeți.",
      },
      {
        title: "Mai multe magazine, o singură salarizare",
        body: "Angajații se mută între puncte de lucru, cei cu program parțial lucrează în două locații. Adunarea orelor pe fiecare magazin și îmbinarea lor manuală costă o zi în fiecare lună.",
      },
      {
        title: "Pauzele și orele suplimentare rămân la aproximație",
        body: "Pauzele legale se scad din memorie, iar orele în plus se decontează informal — exact primele două lucruri pe care le verifică un control.",
      },
    ],
    features: [
      {
        title: "Pontare direct în magazin",
        body: "O apăsare pe propriul telefon la începutul turei. Fără terminal la casă, fără card comun, fără condică lângă ușa depozitului.",
      },
      {
        title: "Ore pe magazin, angajați între puncte de lucru",
        body: "Fiecare magazin este un proiect. Cine ține locul în alt punct de lucru se pontează pe acel punct — vedeți orele acolo unde au fost lucrate efectiv.",
      },
      {
        title: "Pauze scăzute automat",
        body: "Setați regula de pauză o singură dată și se aplică fiecărei ture. Orele suplimentare se înregistrează în momentul în care apar, iar fiecare angajat alege plata sau timp liber.",
      },
    ],
    compliance: {
      label: "Pontaj pe ture",
      title: "O evidență solidă a fiecărei ture lucrate",
      body: "Done-it păstrează o înregistrare digitală obiectivă a fiecărei pontări de intrare și ieșire, cu pauze și ore suplimentare — exact evidența cerută de un audit sau de o dispută pe salariu.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Angajații se pontează la începutul turei", body: "O apăsare pe telefon când începe tura. Înlocuirea în alt punct de lucru se pontează pe acel punct." },
      { step: "Vedeți cine este prezent, pe fiecare magazin", body: "O imagine live în toate locațiile — observați o tură de deschidere neacoperită înainte să se deschidă ușile." },
      { step: "Salarizare într-un singur export", body: "Ore, pauze și ore suplimentare pe angajat și pe magazin, exportate gata pentru furnizorul de salarizare." },
    ],
    stats: [
      { value: "1 apăsare", label: "pentru pontare — fără terminal la casă" },
      { value: "Live", label: "vedeți cine este prezent, în toate magazinele" },
      { value: "0", label: "schimburi de tură pierdute între magazin și salarizare" },
    ],
    quote: {
      text: "Două magazine și mulți angajați cu program parțial. Înainte, jumătate din turele de înlocuire se decontau din memorie. Acum fiecare oră ajunge pe magazinul corect, iar salarizarea durează câteva minute.",
      who: "Proprietar, retail · 11 angajați",
      result: "Pregătirea salarizării, de la o zi la sub o oră",
    },
    relatedSlugs: ["hospitality", "beauty", "offices"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "offices",
    name: "Birouri și servicii profesionale",
    headline: "Pontaj pentru birouri — ore pe client și proiect, fără tabele Excel",
    intro: "Contabili, notari, arhitecți, agenții și firme de consultanță își facturează timpul — sau măcar trebuie să știe unde s-a dus. Done-it înregistrează orele pe clientul și proiectul potrivit, de la birou sau din teren.",
    pains: [
      {
        title: "Orele facturabile, reconstruite vineri",
        body: "Timpul notat la sfârșitul săptămânii este timp ghicit. Ore întregi dispar discret între munca făcută și factură.",
      },
      {
        title: "Nu știți cât costă de fapt un client",
        body: "Onorariul a fost agreat acum câteva luni. Dacă munca mai încape în el se vede abia când cineva face totalul — de obicei prea târziu.",
      },
      {
        title: "Munca hibridă face evidența incompletă",
        body: "O parte din echipă e la birou, o parte acasă, o parte la client. Prezența încetează să fie un fapt și devine o reconstituire.",
      },
    ],
    features: [
      {
        title: "Ore pe client și proiect",
        body: "Fiecare înregistrare ajunge pe un proiect, așa că vedeți orele pe fiecare client fără să exportați nimic și fără să întrebați pe nimeni.",
      },
      {
        title: "Funcționează la birou și în deplasare",
        body: "Aceeași aplicație pe telefon sau în browser. Vizita la un client se contorizează ca muncă pentru acel client, inclusiv deplasarea, dacă vreți.",
      },
      {
        title: "Ore suplimentare și concedii într-un singur loc",
        body: "Orele în plus se înregistrează pe loc, concediile se cer și se aprobă în aplicație — fără un tabel paralel cu cine lipsește și când.",
      },
    ],
    compliance: {
      label: "Pontaj pe client",
      title: "O evidență obiectivă a orelor lucrate",
      body: "Done-it păstrează o evidență digitală, protejată la modificări, a timpului de lucru pentru fiecare angajat — baza atât pentru salarizare, cât și pentru o factură pe care o puteți susține în fața clientului.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Deschideți un proiect de client", body: "Îl configurați o singură dată, cu clientul și, dacă facturați pe buget, orele planificate." },
      { step: "Echipa înregistrează timpul pe el", body: "De la birou sau de pe telefon. Fiecare oră ajunge pe un client, nu în memoria cuiva." },
      { step: "Vedeți orele pe client, exportați pentru salarizare", body: "Totaluri live pe fiecare proiect și un export curat pentru salarizare sau facturare la final de lună." },
    ],
    stats: [
      { value: "Pe client", label: "ore, vizibile în timp ce munca este încă în desfășurare" },
      { value: "Birou sau teren", label: "aceeași evidență, pe telefon și în browser" },
      { value: "1 export", label: "pentru salarizare și facturare" },
    ],
    quote: {
      text: "Înainte reconstruiam săptămâna în fiecare vineri. Acum orele sunt deja pe clientul corect și văd ce dosare mănâncă onorariul.",
      who: "Partener, firmă de contabilitate · 8 angajați",
      result: "Ore facturabile recuperate în fiecare lună",
    },
    relatedSlugs: ["retail", "healthcare", "wellness"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "beauty",
    name: "Saloane și îngrijire corporală",
    headline: "Pontaj pentru saloane — ture, program parțial și salarizare fără agendă pe hârtie",
    intro: "Coafori, frizerii, saloane de manichiură și studiouri de înfrumusețare funcționează pe ture și contracte cu timp parțial. Done-it înregistrează cine a lucrat și când, scade pauzele automat și predă salarizării un fișier curat.",
    pains: [
      {
        title: "Contracte cu timp parțial, variante fără sfârșit",
        body: "Trei zile într-o săptămână, patru în următoarea, o sâmbătă schimbată pe o marți. Orele din contract și orele lucrate se depărtează rapid.",
      },
      {
        title: "Agenda de programări nu este o evidență a timpului",
        body: "Programările arată ce s-a rezervat, nu cine a lucrat. Când întreabă salarizarea sau un control, sistemul de rezervări este răspunsul greșit.",
      },
      {
        title: "Orele în plus, decontate informal",
        body: "Faptul că cineva a rămas peste program pentru un ultim client se ține minte, se promite și uneori se uită. Exact genul de scăpare care ajunge dispută.",
      },
    ],
    features: [
      {
        title: "Pontare la salon",
        body: "O apăsare pe propriul telefon la începutul turei — fără terminal comun la recepție, fără listă de semnături lângă oglindă.",
      },
      {
        title: "Pauze și reguli de program parțial, setate o dată",
        body: "Setați o singură dată regula de pauză și orele standard; se aplică fiecărei ture, cu excepții per persoană acolo unde contractul diferă.",
      },
      {
        title: "Ore suplimentare, plătite sau recuperate",
        body: "Orele în plus se înregistrează pe măsură ce sunt lucrate, iar fiecare angajat alege plata sau timp liber compensator — decontat în aplicație, nu din memorie.",
      },
    ],
    compliance: {
      label: "Pontaj digital",
      title: "O evidență care rezistă dincolo de agenda de programări",
      body: "Done-it înregistrează timpul de lucru efectiv al fiecărui angajat, cu pauze și ore suplimentare — separat de rezervări și solid atunci când este verificat.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Angajații se pontează la începutul turei", body: "O apăsare la sosire. Pauzele se scad după regula setată o singură dată." },
      { step: "Vedeți ziua în desfășurare", body: "Cine este prezent, cine este în pauză, cine a rămas peste program — live, fără să întrebați." },
      { step: "Salarizarea primește un fișier curat", body: "Ore lucrate, pauze și ore suplimentare pe angajat, exportate pentru furnizorul de salarizare." },
    ],
    stats: [
      { value: "1 apăsare", label: "pentru pontare — nimic comun, nimic pe hârtie" },
      { value: "Automat", label: "pauzele se scad la fiecare tură" },
      { value: "0", label: "ore peste program decontate din memorie" },
    ],
    quote: {
      text: "Toată lumea lucrează cu program parțial și fiecare săptămână e diferită. Aplicația pur și simplu înregistrează — am încetat să reconstruiesc luna în Excel.",
      who: "Proprietar, salon de coafură · 6 angajați",
      result: "Fără ore contestate la final de lună",
    },
    relatedSlugs: ["wellness", "retail", "hospitality"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "wellness",
    name: "Sănătate și wellness",
    headline: "Pontaj pentru săli de fitness, studiouri și spa-uri — ture, clase și salarizare într-o singură aplicație",
    intro: "Sălile de fitness, studiourile, cabinetele de fizioterapie și spa-urile au program lung și personal care se rotește. Done-it înregistrează cine a acoperit fiecare tură, scade pauzele automat și exportă salarizarea curat.",
    pains: [
      {
        title: "Program lung, înlocuiri permanente",
        body: "Ture de dimineață, clase seara și weekenduri, acoperite de un amestec de angajați cu normă întreagă, program parțial și colaboratori. Planificarea este un plan, nu o evidență.",
      },
      {
        title: "Orele de clasă se amestecă cu orele în sală",
        body: "Un instructor ține două clase și acoperă recepția între ele. Ca să plătiți corect trebuie să știți ce s-a lucrat efectiv, nu ce era planificat.",
      },
      {
        title: "Mai multe locații, o singură administrare",
        body: "Angajații se mută între studiouri. Îmbinarea manuală a orelor lor în fiecare lună este lentă și predispusă la erori.",
      },
    ],
    features: [
      {
        title: "Pontare pe fiecare studio",
        body: "O apăsare pe telefon în locația în care lucrează. Înlocuirea în alt studio ajunge în orele acelui studio.",
      },
      {
        title: "Imagine live a cine este prezent",
        body: "Vedeți sala în toate locațiile, în timp real — o tură de dimineață neacoperită se vede înainte să se deschidă ușile, nu după.",
      },
      {
        title: "Pauze și ore suplimentare rezolvate",
        body: "Reguli de pauză aplicate automat, ore în plus înregistrate pe loc, cu plată sau timp liber compensator pentru fiecare angajat.",
      },
    ],
    compliance: {
      label: "Pontaj pe ture, în mai multe locații",
      title: "O evidență obiectivă pentru fiecare tură și locație",
      body: "Done-it păstrează o evidență digitală a timpului de lucru pe fiecare angajat și pe fiecare locație — baza pentru plată corectă și pentru orice verificare a orelor lucrate.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Angajații se pontează la studio", body: "O apăsare la începutul turei, în locația în care lucrează." },
      { step: "Înlocuirile se văd live", body: "Cine este prezent și în ce locație chiar acum, ca golurile să fie acoperite înainte să observe membrii." },
      { step: "Un singur export pentru salarizare", body: "Ore, pauze și ore suplimentare pe angajat și pe studio, gata pentru furnizorul de salarizare." },
    ],
    stats: [
      { value: "Pe studio", label: "ore, chiar și când angajații acoperă în altă parte" },
      { value: "Live", label: "vedeți sala în toate locațiile" },
      { value: "Automat", label: "pauze și ore suplimentare, fără calcule manuale" },
    ],
    quote: {
      text: "Două studiouri, program lung și multe ture de înlocuire. Acum orele ajung pe locația corectă, iar salarizarea este un singur fișier.",
      who: "Manager, studio de fitness · 14 angajați",
      result: "Planificarea și salarizarea coincid, în sfârșit",
    },
    relatedSlugs: ["beauty", "healthcare", "hospitality"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "hospitality",
    name: "HoReCa",
    headline: "Pontaj pentru cafenele, restaurante și hoteluri — ture, pauze și ore suplimentare, decontate corect",
    intro: "Ture fracționate, închideri târzii și o sâmbătă aglomerată care se prelungește. Done-it înregistrează orele așa cum sunt lucrate efectiv, scade pauzele după regula voastră și dă salarizării un singur fișier curat.",
    pains: [
      {
        title: "Tura nu se termină niciodată când spune planificarea",
        body: "Serviciul se prelungește, închiderea mai ia o jumătate de oră. Minutele acelea sunt muncă reală, iar decontarea lor din memorie e începutul disputelor.",
      },
      {
        title: "Turele fracționate sunt greu de înregistrat",
        body: "Serviciul de prânz, o pauză, apoi cina. Pe hârtie devine un bloc lung sau două aproximări — niciuna nu este adevărul.",
      },
      {
        title: "Fluctuație mare, instruire continuă",
        body: "Personal nou în fiecare sezon. Orice sistem care cere instruire nu supraviețuiește primei săptămâni aglomerate.",
      },
    ],
    features: [
      {
        title: "Pontare la început și la final de serviciu",
        body: "O apăsare la începutul și la finalul fiecărui bloc, așa că turele fracționate sunt înregistrate așa cum s-au petrecut — nu unite într-una singură.",
      },
      {
        title: "Pauze după regula voastră",
        body: "Pauzele obligatorii se scad automat, cu excepții per persoană acolo unde contractul o cere. Fără calcule manuale la final de lună.",
      },
      {
        title: "Ore suplimentare decontate în aplicație",
        body: "Jumătatea de oră în plus după închidere se înregistrează în momentul în care este lucrată și se plătește sau se recuperează ca timp liber — angajatul alege.",
      },
    ],
    compliance: {
      label: "Pontaj pentru ture fracționate",
      title: "O evidență solidă a orelor într-o echipă cu fluctuație mare",
      body: "Done-it păstrează o înregistrare digitală obiectivă a fiecărei ture, pauze și ore în plus — exact ce cere să vadă o verificare de salarizare sau un control de la inspecția muncii.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Angajații se pontează pentru serviciu", body: "O apăsare la începutul blocului; la fel la final. Turele fracționate rămân fracționate." },
      { step: "Vedeți sala live", body: "Cine este prezent pentru serviciul de diseară, cine este încă în pauză, cine a rămas după închidere." },
      { step: "Salarizarea primește orele reale", body: "Timp lucrat, pauze și ore suplimentare pe angajat, exportate într-un singur fișier." },
    ],
    stats: [
      { value: "Ture fracționate", label: "înregistrate ca două blocuri, nu ca o aproximare" },
      { value: "Automat", label: "pauzele se scad după regula voastră" },
      { value: "1 min", label: "pentru a integra un angajat nou — e propriul lui telefon" },
    ],
    quote: {
      text: "Închiderea se prelungește mereu. Acum minutele acelea sunt înregistrate, nu discutate în contradictoriu, iar angajații le pot recupera ca timp liber.",
      who: "Proprietar, restaurant · 17 angajați",
      result: "Mai puține dispute, salarizare mai rapidă",
    },
    relatedSlugs: ["retail", "wellness", "beauty"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "healthcare",
    name: "Sănătate și îngrijire",
    headline: "Pontaj pentru echipele de îngrijire — ture, vizite la domiciliu și calificări, într-o singură evidență",
    intro: "Cabinetele, echipele de îngrijire la domiciliu și căminele de bătrâni funcționează pe ture, vizite și evidențe stricte. Done-it înregistrează timpul lucrat pe fiecare tură și pe fiecare vizită și păstrează documentele angajaților acolo unde le găsiți.",
    pains: [
      {
        title: "Vizitele și turele, în sisteme separate",
        body: "Vizitele la domiciliu stau într-un instrument, orele planificate în altul, iar salarizarea primește o a treia versiune. Reconcilierea lor este o muncă lunară.",
      },
      {
        title: "Orele de noapte și de weekend, plătite greșit",
        body: "Orele în afara programului normal au tarife diferite. Când evidența este aproximativă, la fel este și plata — iar corecția vine mai târziu.",
      },
      {
        title: "Certificatele expiră pe tăcute",
        body: "Calificările și certificatele contează în îngrijire, iar ele stau într-un dosar pe care nu îl deschide nimeni până când întreabă un control.",
      },
    ],
    features: [
      {
        title: "Pontare pe tură sau pe vizită",
        body: "O apăsare la începutul turei sau la fiecare adresă de client pentru îngrijirea la domiciliu — cu locație verificată acolo unde aveți nevoie de dovadă.",
      },
      {
        title: "Ore împărțite așa cum le plătiți",
        body: "Orele de noapte, de weekend și cele normale sunt înregistrate distinct, așa că exportul pentru salarizare reflectă deja modul real de plată.",
      },
      {
        title: "Documentele angajaților într-un singur loc",
        body: "Contracte, certificate și acte de identitate stocate lângă persoana căreia îi aparțin — găsite în câteva secunde când le cere cineva.",
      },
    ],
    compliance: {
      label: "Pontaj pe ture și vizite",
      title: "Evidențe care trec de un control",
      body: "Done-it păstrează o evidență digitală și obiectivă a timpului de lucru pentru fiecare angajat — ore, pauze și separarea orelor în afara programului normal — alături de documentele care aparțin fiecărei persoane.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Pontare pentru tură sau vizită", body: "O apăsare la locație sau la adresa clientului, cu locație verificată acolo unde este nevoie de dovadă." },
      { step: "Orele ajung în categoria corectă", body: "Orele normale, de noapte și de weekend sunt înregistrate distinct, pe măsură ce sunt lucrate." },
      { step: "Export pentru salarizare", body: "Un singur fișier curat pe perioadă, deja împărțit așa cum îl plătiți." },
    ],
    stats: [
      { value: "Pe vizită", label: "sau pe tură — cum funcționează echipa voastră" },
      { value: "Separate", label: "orele de noapte și de weekend, înregistrate ca lucrate" },
      { value: "Arhivate", label: "certificate stocate pe fiecare angajat" },
    ],
    quote: {
      text: "Vizitele la domiciliu și turele planificate stau în sfârșit în aceeași evidență. Salarizarea a încetat să fie un exercițiu de reconciliere.",
      who: "Coordonator, echipă de îngrijire la domiciliu · 22 angajați",
      result: "O singură evidență în loc de trei",
    },
    relatedSlugs: ["wellness", "offices", "cleaning"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "gps-tracking-field-workers-privacy-gdpr",
    ],
  },
];
